/**
 * Federation Impact — the demo headline. Combines the four Phase 7 builds
 * into a single coherent story:
 *
 *   1. Federation rollup (CCHI = 17 CBOs as one cohort)
 *   2. Member vulnerability composite (HIGH/MEDIUM/LOW bands)
 *   3. Outcome calibration (clicks × 0.93 → nav supports, replies × 1.07 → enrollments)
 *   4. External benchmark comparison (KFF, DHCS, Pew, CMS, CHCS)
 *
 * Internal CBO only — all four endpoints 403 for non-internal callers.
 */

import React, { useMemo, useState } from 'react';
import {
  Users, ShieldAlert, TrendingUp, ExternalLink, Activity, Info,
  ChevronDown, ChevronRight, BookOpen,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import {
  useFederationRollup,
  useVulnerabilityByFederation,
  useOutcomeCalibration,
  useExternalBenchmarks,
  useSdohOverlay,
  type BenchmarkRow,
} from '@/hooks/ml';
import { EngagementFunnelChart } from './charts/EngagementFunnelChart';
import { VulnerabilityRadarChart } from './charts/VulnerabilityRadarChart';

// Backend returns numeric columns as strings (Decimal → JSON). Coerce
// defensively before any .toFixed() / Math.round() — otherwise a single
// .toFixed() on a string throws and React 18 unmounts the entire panel.
const num = (v: unknown): number | null => {
  if (v == null) return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
};

const fmt = (v: unknown): string => {
  const n = num(v);
  return n == null ? '—' : Math.round(n).toLocaleString();
};

const pct = (v: unknown): string => {
  const n = num(v);
  return n == null ? '—' : `${n.toFixed(2)}%`;
};

const BAND_COLOR: Record<string, string> = {
  HIGH: 'bg-rose-500',
  MEDIUM: 'bg-amber-500',
  LOW: 'bg-emerald-500',
};

export const FederationImpactCard: React.FC = () => {
  const fed = useFederationRollup();
  const vuln = useVulnerabilityByFederation();
  const [selectedFed, setSelectedFed] = useState<string | null>(null);
  const calib = useOutcomeCalibration(selectedFed);
  const bench = useExternalBenchmarks();
  const sdoh = useSdohOverlay();
  // (legacy bottom-pane toggle removed — benchmarks are now woven inline)

  // Benchmarks keyed for inline contextual rendering.
  // IMPORTANT: declared before any early-return branches below so hook order
  // is identical across renders (avoids prod React error #310).
  const benchByKey = useMemo(() => {
    const map: Record<string, BenchmarkRow> = {};
    for (const b of bench.data?.benchmarks ?? []) {
      map[b.metric_key] = b;
    }
    return map;
  }, [bench.data]);

  // Default to the largest federation once we have data
  const federations = fed.data?.federations ?? [];
  const activeFed = selectedFed ?? federations[0]?.federation_code ?? null;
  const activeRow = federations.find((f) => f.federation_code === activeFed);

  if (fed.isLoading) {
    return (
      <Card>
        <CardContent className="pt-4 text-sm text-gray-500">
          Loading federation rollup…
        </CardContent>
      </Card>
    );
  }

  if (fed.isError || !federations.length) {
    return (
      <Card>
        <CardContent className="pt-4 text-sm text-gray-500">
          Federation rollup unavailable. (This view is internal-CBO only.)
        </CardContent>
      </Card>
    );
  }

  const vulnRows = (vuln.data?.rows ?? []).filter((r) => r.federation_code === activeFed);
  const vulnTotal = vulnRows.reduce((a, r) => a + r.members, 0);
  const calibTotal = calib.data?.totals?.[0];
  const factors = calib.data?.active_factors ?? [];

  // Population Vulnerability Index for this federation — weighted across
  // HIGH/MEDIUM bands only (LOW contributes 0). Reaching HIGH/MEDIUM
  // members is harder, so engagement rate is lifted by (1 + PVI/100).
  const pvi: number | null = (() => {
    if (!vulnTotal) return null;
    const weighted = vulnRows.reduce(
      (a, r) =>
        a +
        r.members *
          (r.vulnerability_band === 'HIGH'
            ? 80
            : r.vulnerability_band === 'MEDIUM'
            ? 45
            : 0),
      0,
    );
    return weighted / vulnTotal;
  })();
  const rawRate = num(activeRow?.engagement_rate_pct);
  const adjustedRate =
    rawRate != null && pvi != null ? rawRate * (1 + pvi / 100) : null;
  const liftMultiplier = pvi != null ? 1 + pvi / 100 : null;

  // Active calibration factors for the visible federation
  const replyFactor = factors.find(
    (f) => f.federation_code === activeFed && f.driver_metric === 'reply',
  );
  const clickFactor = factors.find(
    (f) => f.federation_code === activeFed && f.driver_metric === 'click',
  );

  return (
    <Card>
      <CardContent className="pt-4 space-y-4">
        {/* Federation selector */}
        {federations.length > 1 && (
          <div className="flex items-center gap-2 text-xs">
            <span className="text-gray-500">Federation:</span>
            {federations.map((f) => (
              <button
                key={f.federation_code}
                type="button"
                onClick={() => setSelectedFed(f.federation_code)}
                className={`rounded-full border px-3 py-1 font-medium ${
                  f.federation_code === activeFed
                    ? 'border-indigo-300 bg-indigo-50 text-indigo-900'
                    : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                {f.federation_code} ({f.cbo_count})
              </button>
            ))}
          </div>
        )}

        {/* Headline row — top-of-page metrics; benchmarks live in the appendix below */}
        {activeRow && (
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <HeadlineMetric
              icon={<Users className="h-4 w-4 text-indigo-500" />}
              label="Unique members engaged"
              value={fmt(activeRow.unique_members_engaged)}
              sub={`${fmt(activeRow.members_total)} in registry`}
            />
            <HeadlineMetric
              icon={<TrendingUp className="h-4 w-4 text-emerald-500" />}
              label="Total sends"
              value={fmt(activeRow.sends)}
              sub={`${fmt(activeRow.cbo_count)} CBOs, since ${activeRow.first_send_ts?.slice(0, 7) ?? '—'}`}
            />
            <HeadlineMetric
              icon={<TrendingUp className="h-4 w-4 text-violet-500" />}
              label="Engagement rate (PVI-adjusted)"
              value={
                adjustedRate != null
                  ? pct(adjustedRate)
                  : pct(activeRow.engagement_rate_pct)
              }
              sub={
                adjustedRate != null && pvi != null && liftMultiplier != null
                  ? `${liftMultiplier.toFixed(2)}× difficulty lift (PVI ${pvi.toFixed(0)} · HIGH+MED only)`
                  : `${pct(activeRow.reply_rate_pct)} reply · ${pct(activeRow.click_rate_pct)} click`
              }
            />
            <HeadlineMetric
              icon={<ShieldAlert className="h-4 w-4 text-rose-500" />}
              label="Hard-to-reach proxies"
              value={`${Math.round((activeRow.members_non_english / Math.max(1, activeRow.members_total)) * 100)}% LEP`}
              sub={[
                `${Math.round((activeRow.members_no_email / Math.max(1, activeRow.members_total)) * 100)}% no-email`,
                activeRow.members_no_postal != null
                  ? `${Math.round((activeRow.members_no_postal / Math.max(1, activeRow.members_total)) * 100)}% no-postal`
                  : null,
                activeRow.members_phone_only != null
                  ? `${Math.round((activeRow.members_phone_only / Math.max(1, activeRow.members_total)) * 100)}% phone-only`
                  : null,
              ]
                .filter(Boolean)
                .join(' · ')}
            />
          </div>
        )}

        {/* Side-by-side visual overlays: engagement funnel + vulnerability radar */}
        {activeRow && (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div className="rounded-md border border-gray-200 bg-white p-3">
              <div className="text-xs font-medium text-gray-700 mb-2">
                Engagement funnel — sends → reach → projected outcomes
              </div>
              <EngagementFunnelChart
                row={activeRow}
                projectedEnrollments={calibTotal?.projected_enrollments}
                enrollmentRatio={replyFactor?.ratio ?? null}
                enrollmentLabel={replyFactor?.outcome_label ?? null}
              />
            </div>
            <div className="rounded-md border border-gray-200 bg-white p-3">
              <div className="text-xs font-medium text-gray-700 mb-2">
                Cohort difficulty radar — % of members on each hard-to-reach axis
              </div>
              <VulnerabilityRadarChart row={activeRow} />
            </div>
          </div>
        )}

        {/* Phase 10d — reach efficiency, time-to-first-reply, geo concentration */}
        {activeRow && (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <HeadlineMetric
              icon={<TrendingUp className="h-4 w-4 text-sky-500" />}
              label="Reach efficiency"
              value={
                activeRow.reach_efficiency_pct != null
                  ? `${num(activeRow.reach_efficiency_pct)!.toFixed(1)}%`
                  : '—'
              }
              sub={`${fmt(activeRow.unique_members_engaged)} / ${fmt(activeRow.sends)} sends`}
            />
            <HeadlineMetric
              icon={<Activity className="h-4 w-4 text-emerald-500" />}
              label="Median reply latency"
              value={
                activeRow.time_to_first_reply_p50_h != null
                  ? `${num(activeRow.time_to_first_reply_p50_h)!.toFixed(1)}h`
                  : '—'
              }
              sub="send → first reply (within 30d)"
            />
            <HeadlineMetric
              icon={<Users className="h-4 w-4 text-indigo-500" />}
              label="Geographic concentration"
              value={
                activeRow.geo_concentration_top3_pct != null
                  ? `${num(activeRow.geo_concentration_top3_pct)!.toFixed(0)}%`
                  : '—'
              }
              sub={
                activeRow.geo_top3_counties
                  ? `top 3: ${activeRow.geo_top3_counties
                      .split(',')
                      .slice(0, 3)
                      .map((s) => s.trim() || '—')
                      .join(' · ')}`
                  : 'top 3 counties'
              }
            />
          </div>
        )}

        {/* Phase 10f — SDOH + ML retention overlay (matched + proxy) */}
        {(() => {
          const sdohRow = sdoh.data?.rows.find(
            (r) => r.federation_code === activeFed,
          );
          if (!sdohRow) return null;
          const numS = (v: unknown): number | null => {
            if (v == null) return null;
            const n = typeof v === 'number' ? v : Number(v);
            return Number.isFinite(n) ? n : null;
          };
          const matched = numS(sdohRow.sdoh_matched_members) ?? 0;
          const proxy = numS(sdohRow.proxy_members) ?? 0;
          const retentionMatched = numS(sdohRow.retention_matched_members) ?? 0;
          const rbi = numS(sdohRow.retention_burden_index);
          const burdenAdj = numS(sdohRow.burden_adjusted_engagement_rate_pct);
          const rawRateForBurden = num(activeRow?.engagement_rate_pct);
          const totalCovered = matched + proxy;
          if (totalCovered === 0 && rbi == null) return null;

          const matchedTotal = numS(sdohRow.retention_matched_members) ?? 0;
          const matchedBuckets = [
            { label: 'Healthy', n: numS(sdohRow.retention_healthy) ?? 0, color: 'bg-emerald-500' },
            { label: 'Watch', n: numS(sdohRow.retention_watch) ?? 0, color: 'bg-yellow-400' },
            { label: 'At risk', n: numS(sdohRow.retention_at_risk) ?? 0, color: 'bg-amber-500' },
            { label: 'Critical', n: numS(sdohRow.retention_critical) ?? 0, color: 'bg-rose-500' },
            { label: 'Lost', n: numS(sdohRow.retention_lost) ?? 0, color: 'bg-gray-500' },
          ];
          const proxyTotal = numS(sdohRow.proxy_members) ?? 0;
          const proxyBuckets = [
            { label: 'Healthy', n: numS(sdohRow.proxy_healthy) ?? 0, color: 'bg-emerald-400' },
            { label: 'Watch', n: numS(sdohRow.proxy_watch) ?? 0, color: 'bg-yellow-300' },
            { label: 'At risk', n: numS(sdohRow.proxy_at_risk) ?? 0, color: 'bg-amber-400' },
            { label: 'Critical', n: numS(sdohRow.proxy_critical) ?? 0, color: 'bg-rose-400' },
          ];

          // Burden band classifier for the RBI gauge tone
          const rbiBand =
            rbi == null ? 'unknown'
            : rbi >= 70 ? 'extreme'
            : rbi >= 50 ? 'high'
            : rbi >= 30 ? 'moderate'
            : 'low';
          const rbiTone: Record<string, string> = {
            extreme: 'bg-rose-100 text-rose-900 border-rose-300',
            high: 'bg-amber-100 text-amber-900 border-amber-300',
            moderate: 'bg-yellow-100 text-yellow-900 border-yellow-300',
            low: 'bg-emerald-100 text-emerald-900 border-emerald-300',
            unknown: 'bg-gray-100 text-gray-700 border-gray-200',
          };

          return (
            <div className="rounded-md border border-sky-200 bg-sky-50/50 p-3 space-y-3">
              <div className="text-xs font-medium text-sky-900 flex items-center gap-1.5">
                <Activity className="h-3.5 w-3.5" />
                SDOH burden + ML retention overlay
                <span className="text-sky-700/70 font-normal text-[10px] ml-1">
                  ({fmt(matched)} matched · {fmt(proxy)} proxy)
                </span>
              </div>

              {/* Retention Burden Index + burden-adjusted engagement (works for every federation) */}
              {(rbi != null || burdenAdj != null) && (
                <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
                  <div
                    className={`rounded-md border p-3 ${rbiTone[rbiBand]}`}
                  >
                    <div className="text-[10px] uppercase tracking-wide opacity-80">
                      Retention Burden Index
                    </div>
                    <div className="text-2xl font-bold tabular-nums mt-1">
                      {rbi != null ? rbi.toFixed(0) : '—'}
                    </div>
                    <div className="text-[10px] opacity-80">
                      {rbiBand.toUpperCase()} burden · weighted at_risk/critical share
                    </div>
                  </div>
                  <div className="rounded-md border border-violet-200 bg-white p-3">
                    <div className="text-[10px] uppercase tracking-wide text-violet-700">
                      Burden-adjusted engagement
                    </div>
                    <div className="text-2xl font-bold text-violet-900 tabular-nums mt-1">
                      {burdenAdj != null ? `${burdenAdj.toFixed(1)}%` : '—'}
                    </div>
                    <div className="text-[10px] text-violet-700/80">
                      raw {rawRateForBurden.toFixed(2)}% × (1 + RBI/100)
                      {rbi != null && ` = ${(1 + rbi / 100).toFixed(2)}× lift`}
                    </div>
                  </div>
                  <div className="rounded-md border border-gray-200 bg-white p-3 text-[11px] text-gray-600 leading-snug">
                    <div className="text-[10px] uppercase tracking-wide text-gray-500 mb-1">
                      Reading the numbers
                    </div>
                    A high RBI signals the cohort is deeply SDOH-burdened.
                    The burden-adjusted rate credits engagement achieved
                    despite that burden — a 9% raw rate on a 70-RBI cohort
                    is comparable to a ~15% rate on a low-burden cohort.
                  </div>
                </div>
              )}

              {/* Address-derived SDOH (matched members only) */}
              {matched > 0 && (
                <div>
                  <div className="text-[11px] font-medium text-gray-700 mb-1">
                    Address-derived SDOH ({fmt(matched)} matched members via identity_link)
                  </div>
                  <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
                    <SdohCell
                      label="Housing stability"
                      value={numS(sdohRow.avg_housing_stability_score)}
                      suffix=""
                    />
                    <SdohCell
                      label="Housing cost burden"
                      value={numS(sdohRow.avg_housing_cost_burden)}
                      suffix="%"
                    />
                    <SdohCell
                      label="Eviction rate"
                      value={numS(sdohRow.avg_eviction_rate)}
                      suffix=""
                      digits={3}
                    />
                    <SdohCell
                      label="Air quality"
                      value={numS(sdohRow.avg_air_quality_index)}
                      suffix=" AQI"
                    />
                  </div>
                </div>
              )}

              {/* ML retention buckets — matched */}
              {retentionMatched > 0 && (
                <RetentionStack
                  title={`ML retention (matched members, n=${fmt(retentionMatched)})`}
                  buckets={matchedBuckets}
                  total={matchedTotal}
                />
              )}

              {/* Proxy retention buckets — unmatched. Show proportions on
                  hover; the dominant at_risk band is the visual story. */}
              {proxyTotal > 0 && (
                <RetentionStack
                  title={`Retention proxy (unmatched legacy members, n=${fmt(proxyTotal)})`}
                  buckets={proxyBuckets}
                  total={proxyTotal}
                  proxy
                />
              )}
            </div>
          );
        })()}

        {/* Projected realized outcomes — consolidated, no per-band rollup */}
        {calibTotal && (
          <div className="rounded-md border border-violet-200 bg-violet-50 p-3">
            <div className="text-xs font-medium text-violet-900 mb-2 flex items-center gap-1.5">
              <Info className="h-3.5 w-3.5" />
              Projected realized outcomes (calibrated)
            </div>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              <OutcomeCell
                value={fmt(calibTotal.projected_enrollments)}
                label={
                  replyFactor?.outcome_label
                    ? replyFactor.outcome_label.replace(/_/g, ' ')
                    : 'Medi-Cal enrollments'
                }
                formula={`${fmt(calibTotal.replies)} replies × ${
                  replyFactor?.ratio?.toFixed(2) ?? '—'
                }`}
                source={replyFactor?.source}
              />
              <OutcomeCell
                value={fmt(calibTotal.projected_nav_supports)}
                label={
                  clickFactor?.outcome_label
                    ? clickFactor.outcome_label.replace(/_/g, ' ')
                    : 'Navigation supports'
                }
                formula={`${fmt(calibTotal.clicks)} clicks × ${
                  clickFactor?.ratio?.toFixed(2) ?? '—'
                }`}
                source={clickFactor?.source}
              />
              <OutcomeCell
                value={fmt(calibTotal.unique_repliers)}
                label="Unique repliers"
                formula="Distinct members ≥1 reply"
              />
              <OutcomeCell
                value={fmt(calibTotal.unique_clickers)}
                label="Unique clickers"
                formula="Distinct members ≥1 click"
              />
            </div>
          </div>
        )}

        {/* Population Vulnerability Index — HIGH/MEDIUM weighted, LOW=0 */}
        {vulnRows.length > 0 && vulnTotal > 0 && (() => {
          // Weighted across HIGH (80) + MEDIUM (45) only. LOW contributes 0.
          const weightedSum = vulnRows.reduce(
            (a, b) =>
              a +
              b.members *
                (b.vulnerability_band === 'HIGH'
                  ? 80
                  : b.vulnerability_band === 'MEDIUM'
                  ? 45
                  : 0),
            0,
          );
          const pviDisp = Math.round(weightedSum / vulnTotal);
          const highBand = vulnRows.find((b) => b.vulnerability_band === 'HIGH');
          const highPct = highBand ? (highBand.members / vulnTotal) * 100 : 0;
          const vulnBand =
            pviDisp >= 60 ? 'HIGH' : pviDisp >= 40 ? 'MEDIUM-HIGH' : pviDisp >= 25 ? 'MEDIUM' : 'LOW';

          return (
            <div className="rounded-md border border-rose-200 bg-gradient-to-br from-rose-50 to-rose-50/40 p-3">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 rounded-md bg-rose-100 px-3 py-2">
                  <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-rose-700">
                    <Activity className="h-3 w-3" />
                    Population Vulnerability Index
                  </div>
                  <div className="text-3xl font-bold text-rose-900 tabular-nums">{pviDisp}</div>
                  <div className="text-[10px] text-rose-700">{vulnBand} · 0–100 scale</div>
                </div>
                <div className="flex-1">
                  <div className="text-xs font-medium text-rose-900 mb-1.5 flex items-center gap-1.5">
                    <ShieldAlert className="h-3.5 w-3.5" />
                    Member vulnerability composite — {highPct.toFixed(0)}% of cohort in HIGH band
                  </div>
                  <div className="flex h-5 w-full overflow-hidden rounded border border-rose-200">
                    {vulnRows.map((b) => {
                      const w = (b.members / vulnTotal) * 100;
                      return (
                        <div
                          key={b.vulnerability_band}
                          className={BAND_COLOR[b.vulnerability_band]}
                          style={{ width: `${w}%` }}
                          title={`${b.vulnerability_band}: ${fmt(b.members)} (${w.toFixed(1)}%)`}
                        />
                      );
                    })}
                  </div>
                  <div className="mt-1 text-[10px] text-rose-700/80">
                    Index = Σ(band_midpoint × members) / total. Weights: HIGH=80, MEDIUM=45, LOW=0
                    (only harder-to-reach members contribute upward). Bands derived from a 6-component
                    proxy score (language, email, reachability, age cohort, record completeness,
                    federation membership).
                  </div>
                </div>
              </div>
              <div className="mt-2 grid grid-cols-3 gap-2 text-[11px]">
                {vulnRows.map((b) => (
                  <div key={b.vulnerability_band} className="rounded border border-gray-200 bg-white p-2">
                    <div className="flex items-center gap-1">
                      <span className={`h-2 w-2 rounded-full ${BAND_COLOR[b.vulnerability_band]}`} />
                      <span className="font-medium">{b.vulnerability_band}</span>
                      <span className="text-gray-500">avg {b.avg_score?.toFixed(0) ?? '—'}</span>
                    </div>
                    <div className="mt-1 text-gray-700 tabular-nums">{fmt(b.members)} members</div>
                    <div className="mt-1 text-[10px] text-gray-500 space-y-0.5">
                      <div>{fmt(b.members_non_english)} non-Eng</div>
                      <div>{fmt(b.members_no_email)} no email</div>
                      <div>{fmt(b.members_older_adult)} older adult</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}

        {/* External benchmarks appendix — collapsed by default, like-for-like comparison */}
        <BenchmarkAppendix
          benchmarks={bench.data?.benchmarks ?? []}
          activeRow={activeRow}
          calibTotal={calibTotal}
        />
      </CardContent>
    </Card>
  );
};

const HeadlineMetric: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string;
  sub: string;
}> = ({ icon, label, value, sub }) => (
  <div className="rounded-md border border-gray-200 bg-white p-3">
    <div className="flex items-center gap-1.5 text-xs text-gray-500">
      {icon}
      {label}
    </div>
    <div className="text-xl font-semibold text-gray-900 mt-1 tabular-nums">{value}</div>
    <div className="text-[11px] text-gray-500 mt-0.5">{sub}</div>
  </div>
);

const SdohCell: React.FC<{
  label: string;
  value: number | null;
  suffix?: string;
  sub?: string;
  digits?: number;
}> = ({ label, value, suffix = '', sub, digits = 1 }) => (
  <div className="rounded-md border border-sky-200 bg-white p-2">
    <div className="text-[10px] uppercase tracking-wide text-sky-700">{label}</div>
    <div className="text-lg font-semibold text-sky-900 tabular-nums">
      {value == null ? '—' : `${value.toFixed(digits)}${suffix}`}
    </div>
    {sub && <div className="text-[9px] text-sky-700/70">{sub}</div>}
  </div>
);

const RetentionStack: React.FC<{
  title: string;
  buckets: { label: string; n: number; color: string }[];
  total: number;
  proxy?: boolean;
}> = ({ title, buckets, total, proxy }) => (
  <div>
    <div className="text-[11px] font-medium text-gray-700 mb-1 flex items-center gap-1.5">
      {title}
      {proxy && (
        <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[9px] text-amber-800 font-medium">
          proxy
        </span>
      )}
    </div>
    <div className="flex h-4 w-full overflow-hidden rounded border border-gray-200">
      {buckets.map((b) => {
        if (b.n <= 0) return null;
        const w = total > 0 ? (b.n / total) * 100 : 0;
        return (
          <div
            key={b.label}
            className={b.color}
            style={{ width: `${w}%` }}
            title={`${b.label}: ${b.n.toLocaleString()} (${w.toFixed(1)}%)`}
          />
        );
      })}
    </div>
    <div className="mt-1 flex flex-wrap gap-2 text-[10px] text-gray-600">
      {buckets.map((b) => (
        <span key={b.label} className="inline-flex items-center gap-1">
          <span className={`h-2 w-2 rounded ${b.color}`} />
          {b.label}: <span className="tabular-nums">{b.n.toLocaleString()}</span>
        </span>
      ))}
    </div>
  </div>
);

const OutcomeCell: React.FC<{
  value: string;
  label: string;
  formula: string;
  source?: string;
}> = ({ value, label, formula, source }) => (
  <div>
    <div className="text-2xl font-semibold text-violet-900">{value}</div>
    <div className="text-xs text-violet-700 capitalize">{label}</div>
    <div className="text-[10px] text-violet-600 mt-0.5">{formula}</div>
    {source && (
      <div
        className="text-[10px] text-violet-500 mt-0.5 italic leading-snug"
        title={source}
      >
        {source.length > 80 ? source.slice(0, 80) + '…' : source}
      </div>
    )}
  </div>
);

// ---------------------------------------------------------------------------
// Benchmark appendix — collapsible table with like-for-like comparison
// ---------------------------------------------------------------------------

const usd = (raw: unknown): string => {
  const n = num(raw);
  if (n == null) return '—';
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${Math.round(n).toLocaleString()}`;
};

const benchRange = (b: BenchmarkRow): string => {
  const fmtVal = (raw: unknown) => {
    const v = num(raw);
    if (v == null) return '—';
    if (b.unit === 'usd') return `$${v.toLocaleString()}`;
    if (b.unit === 'pct') return `${v}%`;
    return v.toLocaleString();
  };
  const lo = num(b.value_low);
  const hi = num(b.value_high);
  if (lo != null && hi != null) return `${fmtVal(lo)} – ${fmtVal(hi)}`;
  if (b.value_point != null) return fmtVal(b.value_point);
  return '—';
};

/** Build an "our value" + projection cell for each benchmark row. */
const buildAppendixCells = (
  b: BenchmarkRow,
  activeRow: { engagement_rate_pct?: number | null; unique_members_engaged?: number; members_total?: number; members_non_english?: number; members_no_email?: number; members_in_age_cohort?: number } | null | undefined,
  calibTotal:
    | { projected_enrollments?: number; projected_nav_supports?: number }
    | null
    | undefined,
): { ours: string; projected: string } => {
  let ours = '—';
  let projected = '—';

  switch (b.metric_key) {
    case 'sms_engagement_rate_medicaid':
    case 'ecm_engagement_first_six_months': {
      const er = num(activeRow?.engagement_rate_pct);
      if (er != null) ours = `${er.toFixed(2)}%`;
      break;
    }
    case 'ca_medi_cal_enrollment_total': {
      const e = num(activeRow?.unique_members_engaged);
      if (e != null) ours = `${Math.round(e).toLocaleString()} engaged`;
      break;
    }
    case 'limited_english_proficiency_ca': {
      const total = num(activeRow?.members_total);
      const ne = num(activeRow?.members_non_english);
      if (total && ne != null) ours = `${((ne / total) * 100).toFixed(1)}%`;
      break;
    }
    case 'digital_exclusion_medicaid': {
      const total = num(activeRow?.members_total);
      const noe = num(activeRow?.members_no_email);
      if (total && noe != null) ours = `${((noe / total) * 100).toFixed(1)}%`;
      break;
    }
    case 'fpl_below_200_older_adults': {
      const total = num(activeRow?.members_total);
      const ac = num(activeRow?.members_in_age_cohort);
      if (total && ac != null) ours = `${((ac / total) * 100).toFixed(1)}% older adult`;
      break;
    }
    case 'medi_cal_renewal_rate_outreach_assisted': {
      const pe = num(calibTotal?.projected_enrollments);
      if (pe != null) ours = `${Math.round(pe).toLocaleString()} projected enrollments`;
      break;
    }
    case 'cost_per_outreach_completion_sdoh': {
      const pns = num(calibTotal?.projected_nav_supports);
      const lo = num(b.value_low);
      const hi = num(b.value_high);
      if (pns != null && lo != null && hi != null) {
        ours = `${Math.round(pns).toLocaleString()} projected supports`;
        projected = `${usd(pns * lo)} – ${usd(pns * hi)}`;
      }
      break;
    }
    default:
      break;
  }

  return { ours, projected };
};

const BenchmarkAppendix: React.FC<{
  benchmarks: BenchmarkRow[];
  activeRow: { engagement_rate_pct?: number | null; unique_members_engaged?: number; members_total?: number; members_non_english?: number; members_no_email?: number; members_in_age_cohort?: number } | null | undefined;
  calibTotal: { projected_enrollments?: number; projected_nav_supports?: number } | null | undefined;
}> = ({ benchmarks, activeRow, calibTotal }) => {
  const [open, setOpen] = useState(false);

  const rows = useMemo(
    () => benchmarks.map((b) => ({ b, ...buildAppendixCells(b, activeRow, calibTotal) })),
    [benchmarks, activeRow, calibTotal],
  );

  if (!benchmarks.length) return null;

  return (
    <div className="mt-2 rounded-md border border-gray-200 bg-gray-50/50">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-2 px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-100"
        aria-expanded={open}
      >
        {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        <BookOpen className="h-3.5 w-3.5 text-gray-500" />
        Appendix · External benchmarks &amp; like-for-like comparison
        <span className="text-gray-400">({benchmarks.length})</span>
      </button>
      {open && (
        <div className="overflow-x-auto border-t border-gray-200 bg-white">
          <table className="w-full text-xs">
            <thead className="bg-gray-50 text-[10px] uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Metric</th>
                <th className="px-3 py-2 text-left font-medium">Source · Year</th>
                <th className="px-3 py-2 text-right font-medium">Benchmark</th>
                <th className="px-3 py-2 text-right font-medium">Our value</th>
                <th className="px-3 py-2 text-right font-medium">Projected $</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ b, ours, projected }) => (
                <tr key={b.metric_key} className="border-t border-gray-100 align-top">
                  <td className="px-3 py-2">
                    <div className="font-medium text-gray-900">{b.metric_label}</div>
                    {b.comparison_note && (
                      <div className="mt-0.5 text-[10px] text-gray-500 leading-snug">
                        {b.comparison_note}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-gray-700">
                    {b.source_url ? (
                      <a
                        href={b.source_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-indigo-700 hover:underline"
                      >
                        {b.source_name}
                        <ExternalLink className="h-2.5 w-2.5" />
                      </a>
                    ) : (
                      b.source_name
                    )}
                    <div className="text-[10px] text-gray-500">{b.effective_year}</div>
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-gray-900">
                    {benchRange(b)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-gray-900">{ours}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-medium text-emerald-700">
                    {projected}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="border-t border-gray-100 px-3 py-2 text-[10px] text-gray-500 leading-snug">
            “Projected $” is computed only when a benchmark expresses a per-unit
            cost (e.g., $/completion) and we have a matching projected count.
            For percentage or volume benchmarks the comparison is direct.
          </div>
        </div>
      )}
    </div>
  );
};

export default FederationImpactCard;
